import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCode, faServer, faLaptopCode, 
    faMicrochip, faLock, faDisplay, 
    faTools, faHeartbeat, faTerminal,
    faChartBar, faCog
} from '@fortawesome/free-solid-svg-icons';
import { faAngular } from '@fortawesome/free-brands-svg-icons';
import './ProjectDetail.css';
import paperImg from '../assets/images/paper.png';
import rbcBorealisImg1 from '../assets/images/RBC_Borealis1.png';
import rbcBorealisImg2 from '../assets/images/RBC_Borealis2.png';
import rbcBorealisROC from '../assets/images/RBC_Borealis_ROC.png';
import rbcBorealisDashboard from '../assets/images/RBC_Borealis_Dashboard.png';
import wiresImg from '../assets/images/wires.png';
import preview_image from '../assets/images/preview.png';

// Import STM32 project images
import finalSystemImg from '../assets/images/multimeter.jpg';

// Import SubTrackr project images
import subscriptionsPageImg from '../assets/images/Subscriptions-page.png';
import mysqlImg from '../assets/images/MySQL.png';
import bankImg from '../assets/images/Bank.png';

// Import QuickPoll project images
import quickPollBackendImg from '../assets/images/MongoDB.png';
import testingImg from '../assets/images/testing.png';
import containerImg from '../assets/images/container.png';

// Import finger detection images
import captureDataImg from '../assets/images/capture-data.png';
import jupyterNotebookImg from '../assets/images/jupyter-notebook.png';
import modelGuessImg from '../assets/images/model-guess.png';
import codeForOpencvImg from '../assets/images/code-for-opencv.png';

import {
  PandasIcon,
  AzureIcon,
  MatplotlibIcon,
  KaggleIcon,
  JsonIcon,
  AxiosIcon,
  NumPyIcon,
  CSSIcon,
  GoogleCloudIcon,
  AWSIcon,
  Auth0Icon,
  SolderingIcon,
  PCBDesignIcon,
  DataAnalysisIcon,
  NeuralNetworkIcon,
  TypeScriptIcon,
  MySQLIcon,
  ExpressIcon,
  VueIcon,
  JestIcon,
  DockerIcon,
  SocketIOIcon,
  ViteIcon,
  PiniaIcon,
  PythonIcon,
  CordovaIcon,
  AndroidStudioIcon,
  GradleIcon
} from './icons';

// Complete softSkills array
const softSkills = [
    'Problem Solving', 'User-centered Design', 'Team Collaboration', 'Initiative',
    'Team Leadership', 'Event Coordination', 'Delegation', 'Public Speaking',
    'Time Management', 'Critical Thinking', 'Creativity and Innovation',
    'Event Planning', 'Collaborative Leadership', 'Written Communication',
    'Attention to Detail', 'Scheduling', 'Decision-Making', 'Planning & Execution',
    'Cross-Functional Collaboration', 'Prize & Award Organization',
    'Motivational Speaking', 'Lesson Planning', 'Communication', 'Persuasion',
    'Student Engagement', 'Collaborative Problem Solving', 'Constructive Feedback',
    'Record Keeping', 'Customer Support', 'Organization Skills', 'Multitasking'
];

// Complete getTechIcon function - matches Home.js exactly
const getTechIcon = (tech) => {
    if (softSkills.includes(tech)) return null;
    const normalized = tech
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/\./g, '')
        .replace('(programminglanguage)', '')
        .replace('c++', 'cplusplus');

    switch (normalized) {
        case 'pandas': return PandasIcon;
        case 'cordova':
        case 'apachecordova': return CordovaIcon;
        case 'androidstudio': return AndroidStudioIcon;
        case 'gradle': return GradleIcon;
        case 'python': return PythonIcon;
        case 'numpy': return NumPyIcon;
        case 'matplotlib': return MatplotlibIcon;
        case 'axios': return AxiosIcon;
        case 'kaggle': return KaggleIcon;
        case 'json': return JsonIcon;
        case 'cascadingstylesheets(css)':
        case 'css': 
        case 'css3': return CSSIcon;
        case 'googlecloudplatform(gcp)':
        case 'googlecloudplatform':
        case 'gcp': return GoogleCloudIcon;
        case 'amazonwebservices(aws)':
        case 'amazonwebservices':
        case 'aws': return AWSIcon;
        case 'microsoftazure':
        case 'azure': return AzureIcon;
        case 'digitalauthentication': return Auth0Icon;
        case 'soldering': return SolderingIcon;
        case 'pcbdesign': return PCBDesignIcon;
        case 'typescript': return TypeScriptIcon;
        case 'mysql': return MySQLIcon;
        case 'expressjs':
        case 'express': return ExpressIcon;
        case 'vue3':
        case 'vuejs':
        case 'vue': return VueIcon;
        case 'jest': return JestIcon;
        case 'docker': return DockerIcon;
        case 'socketio':
        case 'socket.io': return SocketIOIcon;
        case 'dataanalysis': return DataAnalysisIcon;
        case 'datavisualization': return DataAnalysisIcon;
        case 'imageclassification':
        case 'cnn':
        case 'neuralnetworks': return NeuralNetworkIcon;

        // FontAwesome fallbacks - ADD THESE IMPORTS AT THE TOP
        case 'holtermonitor': return { iconName: 'heartbeat' }; // Will use FontAwesome
        case 'teaencryption': return { iconName: 'lock' }; // Will use FontAwesome
        case 'uart': return { iconName: 'server' }; // Will use FontAwesome
        case 'adc': return { iconName: 'server' }; // Will use FontAwesome
        case 'i2c': return { iconName: 'server' }; // Will use FontAwesome
        case 'ssd1306oled': return { iconName: 'display' }; // Will use FontAwesome
        case 'stm32cubeide':
        case 'stm32': return { iconName: 'microchip' }; // Will use FontAwesome
        case 'putty': return { iconName: 'terminal' }; // Will use FontAwesome
        case 'visualwebdeveloper': return { iconName: 'laptop-code' }; // Will use FontAwesome
        case 'autocad': return { iconName: 'tools' }; // Will use FontAwesome
        case 'angular19':
        case 'angular': return { iconName: 'angular' }; // Will use FontAwesome
        case 'plaidapi': return { iconName: 'server' }; // Will use FontAwesome
        case 'chartjs': return { iconName: 'chart-bar' }; // Will use FontAwesome
        case 'servos': return { iconName: 'cog' }; // Will use FontAwesome
        case 'restapi': return { iconName: 'server' }; // Will use FontAwesome
        case 'vite': return ViteIcon; // Will use Vite icon
        case 'pinia': return PiniaIcon; // Will use Pinia icon
        
        default: return { iconName: 'code' }; // Default FontAwesome fallback
    }
};

// Complete getDeviconClass function - matches Home.js exactly
const getDeviconClass = (tech) => {
    const techLower = tech.toLowerCase().replace(/\s+/g, '').replace(/\./g, '').replace('(programminglanguage)', '').replace('c++', 'cplusplus');

    const deviconMap = {
        // Programming Languages
        'python': 'devicon-python-plain colored',
        'cplusplus': 'devicon-cplusplus-plain colored',
        'c': 'devicon-c-plain colored',
        'javascript': 'devicon-javascript-plain colored',
        'typescript': 'devicon-typescript-plain colored',
        'html5': 'devicon-html5-plain colored',
        'html': 'devicon-html5-plain colored',
        'css': 'devicon-css3-plain colored',
        'css3': 'devicon-css3-plain colored',
        'cascadingstylesheets(css)': 'devicon-css3-plain colored',
        
        // Frameworks & Libraries
        'reactjs': 'devicon-react-original colored',
        'react': 'devicon-react-original colored',
        'nodejs': 'devicon-nodejs-plain colored',
        'expressjs': 'devicon-express-original colored',
        'express': 'devicon-express-original colored',
        'vuejs': 'devicon-vuejs-plain colored',
        'vue': 'devicon-vuejs-plain colored',
        'vue3': 'devicon-vuejs-plain colored',
        'angular19': 'devicon-angularjs-plain colored',
        'angular': 'devicon-angularjs-plain colored',
        'flask': 'devicon-flask-original colored',
        'django': 'devicon-django-plain colored',
        'bootstrap': 'devicon-bootstrap-plain colored',
        'angularmaterial': 'devicon-materialui-plain colored',
        
        // Databases
        'mongodb': 'devicon-mongodb-plain colored',
        'mysql': 'devicon-mysql-plain-wordmark colored',
        'postgresql': 'devicon-postgresql-plain colored',
        
        // Tools & Platforms
        'git': 'devicon-git-plain colored',
        'github': 'devicon-github-original colored',
        'githubactions': 'devicon-github-original colored',
        'gitlab': 'devicon-gitlab-plain colored',
        'figma': 'devicon-figma-plain colored',
        'vscode': 'devicon-vscode-plain colored',
        'visualstudiocode': 'devicon-vscode-plain colored',
        'jest': 'devicon-jest-plain colored',
        'cordova': null,
        'apachecordova': null,
        'androidstudio': null,
        'gradle': null,
        
        // Cloud & DevOps
        'docker': 'devicon-docker-plain-wordmark colored',
        'heroku': 'devicon-heroku-plain colored',
        'netlify': 'devicon-netlify-plain colored',
        'firebase': 'devicon-firebase-plain colored',
        'aws': 'devicon-amazonwebservices-original-wordmark colored',
        'amazonwebservices(aws)': 'devicon-amazonwebservices-original-wordmark colored',
        'amazonwebservices': 'devicon-amazonwebservices-original-wordmark colored',
        'gcp': 'devicon-googlecloud-plain colored',
        'googlecloudplatform(gcp)': 'devicon-googlecloud-plain colored',
        'googlecloudplatform': 'devicon-googlecloud-plain colored',
        
        // ML & Data Science
        'tensorflow': 'devicon-tensorflow-original colored',
        'keras': 'devicon-keras-plain colored',
        'pytorch': 'devicon-pytorch-original colored',
        'opencv': 'devicon-opencv-plain colored',
        'jupyter': 'devicon-jupyter-plain-wordmark colored',
        'jupyternotebooks': 'devicon-jupyter-plain-wordmark colored',
        'jupyternotebook': 'devicon-jupyter-plain-wordmark colored',
        
        // Hardware & IoT
        'arduino': 'devicon-arduino-plain colored',
        'arduinoide': 'devicon-arduino-plain colored',
        
        // Others
        'json': 'devicon-json-plain colored',
        'nginx': 'devicon-nginx-original colored',
        'redis': 'devicon-redis-plain colored'
    };

    return deviconMap[techLower] || null;
};

const projectData = {
    'custom-dancing-robot': {
        title: 'Custom Dancing Robot',
        period: 'May 2024 – June 2024',
        technologies: 'C++, AutoCAD, Soldering, Servos, PCB Design, Arduino, Arduino IDE, GitHub',
        summary:
            'Designed and fabricated a custom PCB, programmed movement algorithms on Arduino, and used AutoCAD for part design. Completed in three weeks, showcasing strong electrical design and troubleshooting skills.',
        sections: [
            {
                title: 'Distance Detection and OLED Display Integration',
                description:
                    'For my dancing robot project, I began by integrating an ultrasonic sensor with an Arduino Nano to detect objects within a specific range. This enabled the robot to react dynamically by triggering dance movements based on proximity. Inspired by video resources, I added an OLED display to show real-time distance measurements, enhancing interactivity. Later, I expanded this feature by adding a second OLED and programming animations like GIFs to play on the screens, which provided an engaging visual experience. Managing the dual OLED setup required careful configuration of I2C addresses to prevent interference.',
                youtubeUrl: 'https://www.youtube.com/embed/C6aB4md0lp8',
                layout: 'section-left'
            },
            {
                title: 'Adding Music and Troubleshooting Library Conflicts',
                description:
                    'To elevate the entertainment factor, I incorporated a passive buzzer to play popular songs that sync with the robot\'s movements. Adding music introduced challenges, including a library conflict between the NewPing library for distance sensing and the music library, both of which defined "__vector_7." I explored alternative methods to measure distances without NewPing, allowing me to maintain both music playback and distance sensing functionality. This process deepened my understanding of library compatibility and taught me innovative problem-solving techniques.',
                youtubeUrl: 'https://www.youtube.com/embed/_cu7Ec3OSQ8',
                layout: 'section-right'
            },
            {
                title: 'PCB Design and Fabrication',
                description:
                    'With the electronic components finalized, I moved on to designing the robot\'s printed circuit board (PCB). I started by creating a schematic that mapped out connections for each component, then converted it into a foil pattern for etching. Using a copper-clad board, I drilled necessary holes with a drill press and used ferric chloride to etch the board, then carefully tested each trace with a multimeter. Any broken traces were fixed with a soldering iron, ensuring a reliable circuit that could handle the robot\'s functions seamlessly.',
                images: [paperImg, wiresImg],
                layout: 'section-left'
            },
            {
                title: 'Physical Assembly and Structural Design',
                description:
                    'Constructing the robot\'s body involved significant hands-on work. I cut acrylic sheets to shape the body and face using a band saw and refined edges with a benchtop belt sander. To prepare the legs, I drilled holes in metal brackets to mount servo motors securely, then attached the brackets to the robot\'s body for stability. After spray-painting for a polished look, I mounted the PCB on top, connecting wires from the board to all sensors and motors. This careful assembly ensured a sturdy build and integrated functionality.',
                youtubeUrl: 'https://www.youtube.com/embed/3ENRS-sWS-k',
                layout: 'section-right'
            },
            {
                title: 'Servo Calibration and Movement Fine-Tuning',
                description:
                    'For the robot\'s dance movements, I used positional servos for the feet and continuous rotation servos for the legs. Because continuous rotation servos lack true positional control, I calibrated each servo\'s timing to approximate angles. By adding a small delay on anticlockwise motion, I balanced both sides to synchronize with music playback, creating an engaging performance.',
                youtubeUrl: 'https://www.youtube.com/embed/PGa7ZPMQn5A',
                layout: 'section-left'
            }
        ]
    },
    'whaleguard-narw-sdm': {
        title: 'WhaleGuard – NARW Species Distribution Model',
        period: 'Mar 2026 – May 2026',
        technologies: 'Python, XGBoost, Random Forest, scikit-learn, Pandas, Streamlit, OPeNDAP, SHAP, BayesSearchCV, NumPy',
        summary: 'Production ML pipeline predicting North Atlantic Right Whale habitat from satellite oceanographic data. Built with team Neural Network Navigators as part of the RBC Borealis Let\'s SOLVE It Spring 2026 mentorship cohort. Achieved 0.91 ROC-AUC and ≥80% recall at conservation-optimized thresholds, enabling proactive vessel rerouting before whale encounters rather than reacting after.',
        githubUrl: 'https://github.com/ParthB21/WhaleGuard_RBC_LSI',
        sections: [
            {
                title: 'The Problem: 380 Whales, Reactive Systems, and Ship Strikes',
                description: 'The North Atlantic Right Whale (NARW) is one of the world\'s most critically endangered large mammals, with fewer than 380 individuals remaining. Ship strikes are among the leading causes of adult mortality. Current maritime safety systems are reactive — vessels are only alerted when whales are already in their lanes. Our goal with WhaleGuard was to build a predictive habitat model: given real-time satellite-derived oceanographic data, predict where NARWs are likely to be present, enabling Transport Canada and vessel operators to proactively reroute or slow down before encounters occur. We were selected for the RBC Borealis Let\'s SOLVE It Spring 2026 cohort as team Neural Network Navigators.',
                images: [rbcBorealisImg1],
                layout: 'section-left'
            },
            {
                title: 'Building the ETL Pipeline: Pseudo-Absences, OPeNDAP & Feature Engineering',
                description: 'The pipeline begins with whale presence records from the North Atlantic Right Whale Consortium database (verified sightings 2002–2020). Since we only have presence labels, we implemented the Gowan (2014) pseudo-absence generation method — sampling background absence points proportionally to sampling effort across the Gulf of St. Lawrence study region. We then built a high-performance OPeNDAP slab-fetch engine to extract satellite datasets from NOAA ERDDAP: sea surface temperature (SST), chlorophyll-a concentration, sea surface salinity, and bathymetry. The engine uses exponential backoff retries and intermediate checkpointing so multi-hour overnight extractions are resilient to network interruptions. Feature engineering adds SST gradient magnitude (a proxy for thermal fronts where copepods — NARW prey — aggregate), Chl-a 8-day composites, and seasonal time encodings, producing a 10-feature dataset of 64,900 samples.',
                images: [rbcBorealisImg2],
                layout: 'section-right'
            },
            {
                title: 'Model Training, BayesSearchCV & Conservation-Optimized Thresholds',
                description: 'We trained and compared two ensemble models: Random Forest and XGBoost, both tuned via BayesSearchCV (Bayesian hyperparameter optimization) in a stratified 5-fold cross-validation setup. Random Forest achieved 0.91 ROC-AUC; XGBoost achieved 0.90 ROC-AUC on the held-out test set (12,980 samples). Standard classification thresholds (τ = 0.5) are designed to maximize overall accuracy — but in conservation, false negatives (missing a whale) are far more costly than false positives. We implemented a custom threshold-tuning step that sweeps τ from 0.05 to 0.95 and selects the lowest threshold satisfying ≥80% recall. For Random Forest this yielded τ = 0.15, achieving 81.2% recall and 66.4% precision — a deliberate trade-off that appropriately weights whale safety over operational efficiency.',
                images: [rbcBorealisROC],
                layout: 'section-left'
            },
            {
                title: 'Streamlit Dashboard & RBC Borealis Showcase',
                description: 'The final deliverable is an interactive Streamlit dashboard allowing users to input oceanographic parameters and receive real-time habitat probability predictions. A Gulf of St. Lawrence heatmap visualizes model output across the study region, with hotspot overlays for zones exceeding the conservation threshold. We presented WhaleGuard to RBC Borealis AI mentors and other Spring 2026 cohort teams, walking through the dataset, model architecture, SHAP feature importance analysis (SST gradient and depth ranked highest), and live dashboard queries. The project is open source on GitHub under the WhaleGuard_RBC_LSI repository.',
                images: [rbcBorealisDashboard],
                layout: 'section-right'
            }
        ]
    },
    'finger-detection-opencv': {
        title: 'Finger Detection with OpenCV and PyTorch',
        period: 'January 2025',
        technologies: 'Python, OpenCV, PyTorch, Jupyter Notebook, Computer Vision, Neural Networks, Machine Learning',
        summary: 'Developed a machine learning system that can detect and count fingers in real-time video using a custom dataset and a neural network model built with PyTorch and OpenCV.',
        sections: [
            {
                title: 'Why I Started This Project',
                description: 'I began the FingerDetectionOpenCV project to explore how machine learning and computer vision can be combined to recognize hand gestures, particularly the number of fingers being held up. This kind of functionality can be applied in gesture-controlled interfaces, accessibility tools, or even embedded systems. I wanted to challenge myself by going beyond using pre-made datasets — so I created my own. I captured the image data myself using my laptop\'s webcam, ensuring privacy and ownership over the dataset. I chose not to upload the dataset online, as I preferred to keep the images private and not share personal data with the public. I worked on this project using Visual Studio Code and Jupyter Notebook on my personal laptop.',
                images: [captureDataImg],
                layout: 'section-left'
            },
            {
                title: 'What the Code Does',
                description: 'The project has two major components: data collection and model training. The data collection script (capture-images.py) uses OpenCV to stream video from the webcam and allows users to save labeled images of their hand gestures (0 to 5 fingers) by pressing corresponding keys. These images are saved in organized folders. The second part, built in a Jupyter Notebook, processes this dataset using PyTorch. It first splits the images into training and testing sets, applies grayscale and normalization transforms, and uses a fully connected neural network to classify images into one of six classes. The model is trained using cross-entropy loss and evaluated based on accuracy and average test loss.',
                images: [jupyterNotebookImg],
                layout: 'section-right'
            },
            {
                title: 'What the Results Were',
                description: 'After training on my own dataset, the model achieved reasonable accuracy in classifying the number of fingers shown in the test images. The performance varied depending on the lighting, background, and clarity of hand placement — which I expected given the variability in my data collection setup. However, the model consistently recognized simple gestures (like 0 or 5) with high confidence and demonstrated the core capability I intended to build: gesture classification based on visual input.',
                images: [modelGuessImg],
                layout: 'section-left'
            },
            {
                title: 'What I Learned',
                description: 'This project taught me how to work with custom datasets, which is very different from using standardized datasets like MNIST or CIFAR. I learned how to implement and fine-tune image preprocessing, manage dataset splits, and handle real-time data collection with OpenCV. It also gave me a deeper understanding of how neural networks interpret visual patterns and how crucial data quality is for training accuracy. Most importantly, it strengthened my ability to build a complete pipeline — from data capture to model deployment — and helped me appreciate the challenges and opportunities in building computer vision tools from scratch.',
                images: [codeForOpencvImg],
                layout: 'section-right'
            }
        ]
    },
    'stm32-ecg-signal-transmission': {
        title: 'STM32 ECG Signal Transmission Prototype',
        period: 'October 2024 – November 2024',
        technologies: 'C, STM32CubeIDE, STM32 HAL, ADC, UART, I2C, TEA Encryption, SSD1306 OLED, PuTTY, GitHub',
        summary: 'Developed a secure ECG signal transmission system using STM32 microcontrollers, implementing ADC for potentiometer-based signal simulation, TEA encryption, half-duplex UART communication, and OLED displays. Completed in six months with teammate Eugene Zhao for ECE 198: Project Studio, showcasing embedded systems programming, hardware integration, and debugging skills.',
        sections: [
            {
                title: 'Studying UART Half-Duplex Communication',
                description: 'The STM32 ECG project began with learning the fundamentals of STM32 microcontrollers, as required by the ECE 198 curriculum for implementing a communication mechanism. Eugene and I watched numerous tutorials, but a YouTube video on STM32 UART half-duplex communication stood out. It demonstrated connecting two STM32 boards (like our STM32F446RE and STM32F401RE) via a single wire for bidirectional data transfer, using a button to trigger transmission. This tutorial provided a clear foundation for our project, guiding us to configure UART with interrupts and manage transmit/receive modes, setting the stage for our secure ECG transmission system.',
                youtubeUrl: 'https://www.youtube.com/embed/tbTM5O0PhiU',
                layout: 'section-left'
            },
            {
                title: 'Implementing UART Communication Between STM32s',
                description: 'Inspired by the tutorial, we configured half-duplex UART communication between the STM32F446RE (sender) and STM32F401RE (receiver) using a single wire on PA9 at 115200 baud. We programmed a button (PC13) to trigger data transmission, switching between transmit and receive modes with interrupts. Early attempts faced synchronization issues, as it was our first time using STM32s. Through persistent debugging with STM32CubeIDE\'s live expressions, we achieved our first successful data transfer. This breakthrough, marked by our excitement, confirmed reliable communication over 1 meter, laying the groundwork for adding signal simulation and encryption.',
                youtubeUrl: 'https://www.youtube.com/embed/l5nUqWRQN44',
                layout: 'section-right'
            },
            {
                title: 'Setting Up Potentiometer and ADC for ECG Simulation',
                description: 'To simulate ECG signals, I wired a 10kΩ potentiometer to the STM32F446RE\'s ADC (PA0, Channel 0), powering it via the STM32\'s 3.3V and ground pins. I configured the 12-bit ADC to read voltages (0–3.3V) and mapped them to OLED y-positions (0–63 pixels). Using PuTTY on COM3, we monitored voltage and y-position values while twisting the potentiometer, mimicking heartbeat-like signals due to ECE 198\'s no-human-testing rule. Initial ADC noise was resolved by adjusting sampling times, and multimeter tests confirmed ±5% accuracy, ensuring reliable signal input for transmission.',
                youtubeUrl: 'https://www.youtube.com/embed/bjmdM3gH32U',
                layout: 'section-left'
            },
            {
                title: 'Integrating OLED for Signal Visualization',
                description: 'I added an SSD1306 OLED display to the STM32F446RE via I2C (400 kHz) to visualize simulated ECG signals. I programmed a scrolling graph to plot voltage levels in real-time and implemented a numerical voltage display mode. Early issues included a flipped graph (0V at maximum), which I fixed by correcting the y-position mapping. I also rewrote the OLED library to support buffer shifting and vertical line drawing for smooth scrolling. This setup provided an engaging visualization of the potentiometer\'s output, making the prototype interactive and intuitive.',
                youtubeUrl: 'https://www.youtube.com/embed/ynJvyRNQDxw',
                layout: 'section-right'
            },
            {
                title: 'Adding TEA Encryption and Verifying Data Transfer',
                description: 'We implemented TEA encryption (32 cycles, 128-bit key) to secure data transfer. I coded encryption on the sender to process 8-byte ADC data blocks and decryption on the receiver to recover voltages. To verify transfer, we initially removed encryption, using STM32CubeIDE\'s live expressions to confirm voltage values reached the receiver while Eugene twisted the potentiometer. Adding a second OLED to the receiver displayed decrypted voltages, but buffer alignment issues required clearing data buffers. After resolving these, we confirmed identical voltage graphs across both STM32s, ensuring secure transmission.',
                youtubeUrl: 'https://www.youtube.com/embed/X9aGWWodkJ4',
                layout: 'section-left'
            },
            {
                title: 'Finalizing System with Voltage Verification',
                description: 'For the final system, I integrated ADC, UART, TEA, and dual OLEDs, displaying synchronized voltage graphs on both STM32s. I added a button (PC13) to toggle between graphical and encrypted value displays on both OLEDs, enhancing interactivity. Multimeter tests verified that the sender\'s voltage readings matched the receiver\'s decrypted outputs, confirming end-to-end accuracy. Debugging I2C stability and optimizing interrupts ensured reliability, with the system consuming 0.64W. The finalized demo, showcasing real-time voltage tracking over 1 meter, met ECE 198\'s goals for secure, PHIPA-inspired signal transmission.',
                youtubeUrl: 'https://www.youtube.com/embed/F13gJO3H_EI',
                images: [finalSystemImg],
                layout: 'section-mixed-media'  // Special layout for combined video and image
            }
        ]
    },
    'subtrackr-subscription-tracker': {
        title: 'SubTrackr: Subscription Tracking Web Application',
        period: 'May 2025',
        technologies: 'Angular 19, TypeScript, Node.js, Express, MySQL, Plaid API, Chart.js, Angular Material, REST API',
        summary: 'SubTrackr is a full-stack web application I built to help users track and manage recurring subscriptions, featuring a dashboard for spending summaries, subscription management tools, financial analytics, and Plaid API integration for detecting recurring payments from bank transactions.',
        sections: [
            {
                title: 'Crafting a Dynamic Frontend with Angular and TypeScript',
                description: 'SubTrackr\'s frontend was my first deep dive into Angular 19 and TypeScript, a leap from smaller practice projects I\'d done to learn the framework. I used Angular Material to create a responsive, polished UI, with components like the dashboard displaying spending stats and a subscription list for managing entries. TypeScript\'s strict typing helped me write cleaner code, while ng2-charts powered interactive visualizations like pie and bar charts. Building reusable components and handling API calls with Angular\'s HttpClient taught me component-based architecture and reactive programming, making the interface both functional and engaging.',
                images: [subscriptionsPageImg],
                layout: 'section-right'
            },
            {
                title: 'Developing a Backend and Database with Node.js and MySQL',
                description: 'The backend, built with Node.js, Express, and MySQL, was my first serious exploration of server-side development and relational databases, expanding on basic MySQL projects I\'d tackled before. I designed REST API endpoints for subscriptions, payments, and Plaid integration, connecting them to a MySQL database with tables for users, subscriptions, and transaction suggestions. Using MySQL Workbench to craft the schema and node-cron for scheduling tasks like payment reminders taught me about API design, database normalization, and automation, though debugging connection issues was a steep but rewarding learning curve.',
                images: [mysqlImg],
                layout: 'section-left'
            },
            {
                title: 'Integrating Plaid API for Automated Subscription Detection',
                description: 'Integrating the Plaid API to detect recurring payments was a highlight of SubTrackr and my first experience working with a financial API. In the sandbox environment, I built endpoints to create Plaid Link tokens, exchange public tokens, and fetch transactions, processing them to suggest subscriptions based on recurring charges. On the frontend, I used TypeScript to manage the Plaid Link flow, triggered via a dashboard button. This process, built on small API experiments I\'d done before, taught me secure token handling and transaction processing, though the sandbox\'s limitations highlighted the need for production-grade security.',
                images: [bankImg],
                layout: 'section-right'
            },
            {
                title: 'Visualizing Financial Insights with Analytics',
                description: 'SubTrackr\'s analytics component, my first attempt at data visualization in Angular, uses ng2-charts and Chart.js to display spending by category (pie charts) and monthly trends (bar charts). I connected these charts to backend APIs that aggregate subscription data, learning to optimize API calls for real-time updates. This feature, built after experimenting with basic TypeScript charting projects, made SubTrackr a powerful financial tool, teaching me how to balance data accuracy with visual clarity while navigating Angular\'s data-binding system.',
                youtubeUrl: 'https://www.youtube.com/embed/nC6cU7PK5vg',
                layout: 'section-left'
            },
            {
                title: 'Addressing Challenges and Future Growth',
                description: 'SubTrackr, as a prototype, lacks user authentication and production-ready security, challenges I tackled while learning Angular, TypeScript, and MySQL. Debugging Plaid\'s token exchange and refining the transaction suggestion algorithm tested my problem-solving skills, building on smaller debugging exercises I\'d done. These hurdles taught me the importance of scalability and error handling. I plan to add authentication, encrypt Plaid data, and implement email notifications, using this project as a foundation for more advanced full-stack development.',
                youtubeUrl: 'https://www.youtube.com/embed/LCfBDMZvjbk',
                layout: 'section-right'
            }
        ]
    },
    'quickpoll-vue-realtime-polling': {
        title: 'QuickPoll – Real-Time Polling Application',
        period: 'April 2025',
        technologies: 'Vue 3, TypeScript, Vite, Pinia, Socket.IO, Jest, Docker, GitHub Actions',
        summary: 'QuickPoll is a full-stack web app that lets users create custom polls, vote in real time, and view live results across devices. Built with Vue 3, TypeScript, Socket.IO for real-time updates, and containerized with Docker.',
        liveUrl: 'https://yeehawmcfly.github.io/QuickPoll/',
        sections: [
            {
                title: 'Crafting a Dynamic Frontend with Vue and TypeScript',
                description: 'I built a responsive SPA using Vue 3\'s Composition API and TypeScript with Vite for fast development. The app features smooth page transitions via PageTransition.vue and state management with Pinia. The poll listing in PollList.vue subscribes to real-time Socket.IO events for instant updates, while PollDetail.vue shows live vote progress bars and percentages. The authentication flow in Login.vue and Register.vue includes client-side validation and error handling, all styled with modern CSS variables for a cohesive design system.',
                youtubeUrl: 'https://www.youtube.com/embed/ngKDmf5jA2Y',
                layout: 'section-left'
            },
            {
                title: 'Developing a Secure Backend with Express and MongoDB',
                description: 'The Express backend implements JWT authentication and MongoDB integration through Mongoose models. The User model in userModel.ts handles bcrypt password hashing, while the Poll model in pollModel.ts enforces validation rules like requiring at least two options. The API routes in pollRoutes.ts support creating, retrieving, and voting on polls, with middleware in auth.ts verifying JWT tokens. When votes are cast, the server emits Socket.IO events to all connected clients, enabling the real-time experience that makes QuickPoll engaging.',
                images: [quickPollBackendImg],
                layout: 'section-right'
            },
            {
                title: 'Real-Time Updates with Socket.IO Integration',
                description: 'The heart of QuickPoll\'s interactive experience is Socket.IO, enabling instant vote synchronization across multiple devices. I implemented event handlers for "poll:vote" and "poll:update" in socketHandlers.ts, ensuring all connected clients receive updates when votes are cast. This creates an engaging, interactive polling experience where users can watch results change in real time without requiring page refreshes—demonstrating the power of WebSockets for creating truly interactive applications.',
                youtubeUrl: 'https://www.youtube.com/embed/DdcMO2vwico',
                layout: 'section-left'
            },
            {
                title: 'Ensuring Quality with Jest Tests',
                description: 'I implemented comprehensive tests using Jest and Supertest to ensure reliable application behavior. The test suite in pollModel.test.ts verifies that polls are created with properly initialized vote counters and validates that incomplete polls are rejected. Route tests in pollRoutes.test.ts confirm that API endpoints handle proper requests and errors correctly. This test-driven approach caught several edge cases early in development, particularly around concurrent voting scenarios and authentication flows.',
                images: [testingImg],
                layout: 'section-right'
            },
            {
                title: 'Containerization & CI/CD Pipeline',
                description: 'I containerized the application using Docker with separate Dockerfiles for frontend and backend components. The client uses a multi-stage build process where the Vue app is built in one stage and served by Nginx in another, with custom configuration for SPA routing. Docker Compose orchestrates the entire stack including MongoDB, making deployment consistent across environments. The GitHub Actions workflow automatically tests the server, builds the client, and pushes images to Docker Hub on successful merges to main, creating a streamlined path from development to production.',
                images: [containerImg],
                layout: 'section-left'
            }
        ]
    },
    'photofx-hybrid-mobile-app': {
        title: 'PhotoFX – Hybrid Mobile App with Cordova',
        period: 'March 2025',
        technologies: 'HTML5, CSS3, JavaScript, Apache Cordova, Android Studio, Gradle, Android SDK, Chrome DevTools',
        summary: 'PhotoFX is a hybrid mobile application that allows users to apply visual effects to images in a mobile-style interface. Built entirely with standard web technologies and wrapped into a native Android application using Apache Cordova.',
        sections: [
            {
                title: 'Building a Complete Mobile Development Environment',
                description: 'To develop PhotoFX on Windows without a physical Android device, I configured a comprehensive mobile development environment from scratch. This involved installing Cordova CLI for project scaffolding and platform management, setting up Android Studio to provide the Android SDK and platform tools, and creating a Pixel 6 emulator using the Android Virtual Device Manager. I manually installed and configured Gradle as Android\'s build system, ensuring it was properly added to the system PATH. Critical environment variables like ANDROID_SDK_ROOT and ANDROID_HOME were configured to point to the Android SDK directory, while the PATH was updated to include platform-tools for adb and emulator access, as well as gradle/bin for the build process. This comprehensive setup provided full control over the native Android build pipeline, enabling seamless development from source code to running .apk.',
                images: [preview_image], 
                layout: 'section-left'
            },
            {
                title: 'Hybrid App Development and Native Integration',
                description: 'PhotoFX demonstrates the power of hybrid mobile development by combining web technologies with native Android capabilities. The app features a responsive UI built with standard HTML/CSS that matches modern mobile design patterns, dynamic JavaScript-based photo filters including grayscale, sepia, and invert effects, and smooth touch interactions with component toggles. I integrated Cordova plugins such as cordova-plugin-vibration to access device-specific features, implemented clipboard functionality for enhanced user experience, and created seamless navigation between views with CSS transitions. The app supports simulated camera/photo input and provides real-time filter previews. The development process included building the app using `cordova build android`, launching it in the emulator with `cordova emulate android`, and using Chrome DevTools at chrome://inspect to debug the WebView just like a regular website. I also tested the app as a Progressive Web App by serving it to my iPhone using `cordova serve`, verifying responsive layout and touch interactions across platforms.',
                youtubeUrl: 'https://www.youtube.com/embed/tSqHB83lOhY',
                layout: 'section-right'
            }
        ]
    }
};

const ProjectDetail = () => {
    const { projectId } = useParams();
    const project = projectData[projectId];
    const [selectedImage, setSelectedImage] = useState(null);

    const openImage = (img) => {
        setSelectedImage(img);
    };

    const closeImage = () => {
        setSelectedImage(null);
    };

    if (!project) {
        return <div>Project not found.</div>;
    }

    return (
        <div className="project-detail">
            <h1>{project.title}</h1>
            <p><strong>{project.period}</strong></p>
            <p>{project.summary}</p>

            {/* Add live website link if it exists */}
            {project.liveUrl && (
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <a 
                        href={project.liveUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="live-website-link"
                    >
                        <span className="live-icon">🌐</span>
                        Visit Live Site
                    </a>
                </div>
            )}

            {/* Add the technologies container with icons - same logic as Home.js */}
            <div className="technologies-container">
                <p><strong>Technologies:</strong></p>
                <div className="tech-icons">
                    {project.technologies.split(', ').map(tech => {
                        let iconContent = null;
                        
                        // First try Devicon
                        const deviconClass = getDeviconClass(tech);
                        if (deviconClass) {
                            iconContent = <i className={deviconClass} />;
                        } else {
                            // Then try custom icons
                            const CustomIconOrFAObject = getTechIcon(tech);
                            if (typeof CustomIconOrFAObject === 'function') {
                                const ActualIcon = CustomIconOrFAObject;
                                iconContent = <ActualIcon />;
                            } else if (CustomIconOrFAObject && CustomIconOrFAObject.iconName) {
                                // Handle FontAwesome icon objects
                                const iconName = CustomIconOrFAObject.iconName;
                                switch (iconName) {
                                    case 'heartbeat':
                                        iconContent = <FontAwesomeIcon icon={faHeartbeat} />;
                                        break;
                                    case 'lock':
                                        iconContent = <FontAwesomeIcon icon={faLock} />;
                                        break;
                                    case 'server':
                                        iconContent = <FontAwesomeIcon icon={faServer} />;
                                        break;
                                    case 'display':
                                        iconContent = <FontAwesomeIcon icon={faDisplay} />;
                                        break;
                                    case 'microchip':
                                        iconContent = <FontAwesomeIcon icon={faMicrochip} />;
                                        break;
                                    case 'terminal':
                                        iconContent = <FontAwesomeIcon icon={faTerminal} />;
                                        break;
                                    case 'laptop-code':
                                        iconContent = <FontAwesomeIcon icon={faLaptopCode} />;
                                        break;
                                    case 'tools':
                                        iconContent = <FontAwesomeIcon icon={faTools} />;
                                        break;
                                    case 'angular':
                                        iconContent = <FontAwesomeIcon icon={faAngular} />;
                                        break;
                                    case 'chart-bar':
                                        iconContent = <FontAwesomeIcon icon={faChartBar} />;
                                        break;
                                    case 'cog':
                                        iconContent = <FontAwesomeIcon icon={faCog} />;
                                        break;
                                    case 'code':
                                    default:
                                        iconContent = <FontAwesomeIcon icon={faCode} />;
                                        break;
                                }
                            } else {
                                iconContent = <FontAwesomeIcon icon={faCode} />;
                            }
                        }
                        
                        return (
                            <div className="tech-icon" key={tech}>
                                <span className="tech-icon-wrapper">
                                    {iconContent}
                                </span>
                                <span className="tech-name">{tech}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {project.sections.map((section, idx) => {
                const layoutClass = section.layout || 'section-left';

                // Special case for mixed media layout
                if (layoutClass === 'section-mixed-media') {
                    return (
                        <div key={idx} className="project-section section-mixed-media">
                            <div className="section-content">
                                <h2>{section.title}</h2>
                                <p>{section.description}</p>
                            </div>
                            <div className="media-content mixed-media-container">
                                <div className="video-side">
                                    {section.youtubeUrl && (
                                        <div className="video-container">
                                            <iframe
                                                src={section.youtubeUrl}
                                                title={section.title}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    )}
                                </div>
                                <div className="image-side">
                                    {section.images && section.images.length > 0 && (
                                        <div className="images-container-side">
                                            {section.images.map((img, i) => (
                                                <img
                                                    key={i}
                                                    src={img}
                                                    alt={`${section.title} ${i + 1}`}
                                                    onClick={() => openImage(img)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Regular left/right layouts
                return (
                    <div key={idx} className={`project-section ${layoutClass}`}>
                        {layoutClass === 'section-right' ? (
                            <>
                                <div className="media-content">
                                    {section.youtubeUrl && (
                                        <div className="video-container">
                                            <iframe
                                                src={section.youtubeUrl}
                                                title={section.title}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    )}
                                    {section.images && section.images.length > 0 && (
                                        <div className="images-container">
                                            {section.images.map((img, i) => (
                                                <img
                                                    key={i}
                                                    src={img}
                                                    alt={`${section.title} ${i + 1}`}
                                                    onClick={() => openImage(img)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="section-content">
                                    <h2>{section.title}</h2>
                                    <p>{section.description}</p>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="section-content">
                                    <h2>{section.title}</h2>
                                    <p>{section.description}</p>
                                </div>
                                <div className="media-content">
                                    {section.youtubeUrl && (
                                        <div className="video-container">
                                            <iframe
                                                src={section.youtubeUrl}
                                                title={section.title}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            ></iframe>
                                        </div>
                                    )}
                                    {section.images && section.images.length > 0 && (
                                        <div className="images-container">
                                            {section.images.map((img, i) => (
                                                <img
                                                    key={i}
                                                    src={img}
                                                    alt={`${section.title} ${i + 1}`}
                                                    onClick={() => openImage(img)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
            {selectedImage && (
                <div
                    className="fullscreen-image-modal"
                    onClick={closeImage}
                >
                    <img
                        src={selectedImage}
                        alt="Full size"
                        className="fullscreen-image-content"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <span
                        className="fullscreen-image-close"
                        onClick={closeImage}
                    >
                        ×
                    </span>
                </div>
            )}
        </div>
    );
};

export default ProjectDetail;